import { CommonDialogs, UserLayoutProvider } from "@mcleod/common";
import { Button, ClickEvent, ContextMenuItemCreator, ContextMenuLabelProps, DataSourceRemoteDataChangeEvent, Label, Layout, ModelDataChangeType, Panel, SlideoutDecorator, Snackbar, Table, TableRow, TableSelectionEvent, Toast } from "@mcleod/components";
import { ActionPermission, Alignment, Api, CityUtil, Model, ModelRow, Navigation, ObjectUtil, PermissionsUtil } from "@mcleod/core";
import { BrltlUtil, CarrierSelectionStepper, DispatchUtil, isSpotOrder, LaneHistory, PostToLoadBoards } from "@mcleod/dispatch";
import { CommonContextMenuItems, getDispatchControlValue, LMEGeneralSettings, LMEUserSettings, OrderHoldAction } from "@mcleod/general";
import { BrokerageMovementTableType } from "./BrokerageMovementTableType";
import { CarrierAssignmentStepper } from "./CarrierAssignmentStepper";
import { AutogenLayoutBrokeragePlanningToolPanel } from "./autogen/AutogenLayoutBrokeragePlanningToolPanel";
import { BrokerageMovementTable, instanceOfBrokerageMoveTable } from "./BrokeragePlanningUtil";
import { MultiSendRateConfirmation } from "@mcleod/dispatch/src/planning/MultiSendRateConfirmation";
import { UnassignCarrierPrompt } from "@mcleod/dispatch/src/UnassignCarrierPrompt";
import { MultiCarrierAssignmentStepper } from "./MultiCarrierAssignmentStepper";
import { SendRateConfirmation } from "./SendRateConfirmation";
import { SendLoadTender } from "@mcleod/datafusion";
import { MultiSendLoadTenderStepper } from "@mcleod/dispatch/src/MultiSendLoadTenderStepper";
import { ModelBrokeragePlanningProfile, RowBrokeragePlanningProfile } from "./models/ModelBrokeragePlanningProfile";
import { isReleasedToBilling } from "@mcleod/dispatch/src/Orders";

const ASSIGNED_STATUSES = ["C", "P", "D"];

const INVALID_SELECTION = "One or more selected Movements are invalid for this action";

const ONLY_ONE_ORDER = "Only one order can be selected for this action.";

const INVALID_LOAD_TENDER = "All movements must be assigned to the same carrier.";

const INVALID_DF_CARRIER = "Carrier(s) not set up as a datafusion carrier.";

export class BrokeragePlanningToolPanel extends AutogenLayoutBrokeragePlanningToolPanel {
    table: Table;
    public isContractManagementActive: boolean;
    private static isDatRestControlActive: boolean;
    private static pbwLoadBoardServicesExist: boolean;
    private onHoldAction: OrderHoldAction = new OrderHoldAction();
    private noProfileSnackbar: Snackbar;
    private bpTmProfile: RowBrokeragePlanningProfile;

    override async onLoad(): Promise<void> {
        await this.initializeLoadboardServiceFields();
        await this.initializeBrokeragePlanningProfile();

        this.addMountListener(() => {
            if (instanceOfBrokerageMoveTable(this.owner)) {
                this.owner.tableMovements.addMountListener(() => this.initialize(this.owner));
            }
        });
    }

    private async initializeLoadboardServiceFields(): Promise<void> {
        const result = await Api.search("lme/powerbroker/pbw-loadboard-services", { is_active: "Y" });
        BrokeragePlanningToolPanel.pbwLoadBoardServicesExist = result?.data?.length > 0;
        BrokeragePlanningToolPanel.isDatRestControlActive = result?.data?.some(service => service.service_type === "T");
        this.buttonRefreshLoadPosting.visible = BrokeragePlanningToolPanel.isDatRestControlActive;
    }

    private async initializeBrokeragePlanningProfile(): Promise<void> {
        const bpProfile = new ModelBrokeragePlanningProfile();
        bpProfile.searchSingle({ id: LMEUserSettings.get()?.bp_profile }).then(response => {
            if (response != null) {
                this.bpTmProfile = response.get("int_search_profile_id");
            }
        });
    }

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    initialize(layout?: BrokerageMovementTable) {
        this.table = this.table ?? layout?.tableMovements;
        this.syncButtonVisiblity(layout?.type);
        if (this.getAllVisibleButtons()?.length == 0) {
            this.removeAll();
        }
    }

    async syncButtonVisiblity(tableType: BrokerageMovementTableType) {
        this.buttonAssign.visible = this.determineAssignButtonVisiblity(tableType);
        this.buttonDispatch.visible = true;
        this.buttonLoadboard.visible = tableType === BrokerageMovementTableType.AVAILABLE || tableType === BrokerageMovementTableType.HOLD;
        this.buttonRefreshLoadPosting.visible = tableType === BrokerageMovementTableType.AVAILABLE && BrokeragePlanningToolPanel.isDatRestControlActive;
        this.buttonTopMatch.visible = tableType === BrokerageMovementTableType.AVAILABLE || tableType === BrokerageMovementTableType.HOLD;
        this.buttonConvertSpotToOrder.visible = tableType === BrokerageMovementTableType.AVAILABLE || tableType === BrokerageMovementTableType.HOLD;
        this.buttonLaneHistory.visible = tableType !== BrokerageMovementTableType.HOLD;
        this.buttonReleaseToBilling.visible = tableType === BrokerageMovementTableType.DELIVERED || tableType === BrokerageMovementTableType.HOLD;
        this.buttonCarrierSelection.visible = true;
        this.buttonHold.visible = true;
        this.buttonUpdateField.visible = true;
        this.buttonRemoveCarrier.visible = tableType == BrokerageMovementTableType.COVERED
            || tableType == BrokerageMovementTableType.PROGRESS
            || tableType == BrokerageMovementTableType.DELIVERED
            || tableType == BrokerageMovementTableType.HOLD;
        this.buttonTerminalInfo.visible = await this.determineTerminalButtonVisibility(tableType);
        this.buttonSendRateConfirmation.visible = tableType === BrokerageMovementTableType.COVERED
            || tableType === BrokerageMovementTableType.PROGRESS
            || tableType === BrokerageMovementTableType.DELIVERED || tableType === BrokerageMovementTableType.HOLD;
        this.buttonSendTender.visible = tableType === BrokerageMovementTableType.COVERED
        || tableType === BrokerageMovementTableType.PROGRESS;
    }

    determineAssignButtonVisiblity(tableType: BrokerageMovementTableType) {
        switch (tableType) {
            case BrokerageMovementTableType.HOLD:
            case BrokerageMovementTableType.AVAILABLE:
                return true;
            default:
                return false;
        }
    }

    async determineTerminalButtonVisibility(tableType: BrokerageMovementTableType): Promise<boolean> {
        if (!BrltlUtil.isCompanySettingsBrokerageLTL()) {
            return false;
        }
        const supportedTables = [BrokerageMovementTableType.PROGRESS,BrokerageMovementTableType.COVERED];
        if (!supportedTables.includes(tableType)) {
            return false;
        }
        const response = await Api.search("lme/dispatch/check-contract-management-control-records");
        const data = response?.data[0];
        const isCarrierActive: boolean = data?.carrier_connect_active;
        const isSMC3Active: boolean = data?.smc3_active;
        return isCarrierActive || isSMC3Active;
    }

    onRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        if (event.change.type === ModelDataChangeType.DELETE) {
            const movementId = event.change.data.get("movement_id");
            if (movementId != null && this.table.selectedRow != null) {
                const selectedMoveId = this.table.selectedRow.data.get("movement_id");
                if (movementId === selectedMoveId)
                    this.table.selectedRow = null;
            }
        }
    }

    tableMovementsOnSelect(event: TableSelectionEvent) {
        const tableRow = event.newRow;
        const selectedRows = tableRow?.table?.selectedRows;
        this.getAllVisibleButtons().forEach(button => {
            button.disabledTooltip = null;
            button.enabled = !!selectedRows && !selectedRows.some(row => row.data == null)
            if (button.enabled) {
                this.syncButtonState(button, selectedRows);
            }
        });
    }

    private syncButtonState(button: Button, selectedRows: TableRow[]) {
        if (button === this.buttonLoadboard) {
            this.syncButtonLoadboard(selectedRows);
        } else if (button === this.buttonRefreshLoadPosting) {
            this.syncButtonRefreshLoadPosting(selectedRows);
        } else if (button === this.buttonTopMatch) {
            this.syncButtonTopMatch(selectedRows);
        } else if (button === this.buttonConvertSpotToOrder) {
            this.syncButtonConvertSpotToOrder(selectedRows);
        } else if (button === this.buttonReleaseToBilling) {
            this.syncButtonReleaseToBilling(selectedRows);
        } else if (button === this.buttonLaneHistory) {
            this.syncButtonLaneHistory(selectedRows);
        } else if (button === this.buttonHold) {
            this.syncButtonHold(selectedRows);
        } else if (button === this.buttonRemoveCarrier) {
            this.syncButtonRemoveCarrier(selectedRows);
        } else if (button === this.buttonTerminalInfo || button === this.buttonCarrierSelection) {
            this.syncLtlOrderButton(button, selectedRows);
        } else if (button === this.buttonSendRateConfirmation) {
            this.syncButtonRateCon(selectedRows);
        } else if (button === this.buttonUpdateField) {
            CommonContextMenuItems.syncButtonUpdateField(button, selectedRows);
        } else if (button === this.buttonAssign) {
            this.syncButtonAssignButton(selectedRows);
        } else if (button === this.buttonDispatch) {
            this.syncButtonDispatchButton(selectedRows);
        } else if (button === this.buttonSendTender) {
            this.syncButtonSendTenderButton(selectedRows);
        } else if (button === this.buttonRateOrders) {
            CommonContextMenuItems.syncButtonRateOrders(button, selectedRows);
        }
    }

    private syncButtonLaneHistory(selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            this.buttonLaneHistory.disabledTooltip = ONLY_ONE_ORDER;
            this.buttonLaneHistory.enabled = false;
        }
    }

    private syncButtonRateCon(selectedRows: TableRow[]) {
        if (selectedRows.length > 1 && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTI_RATE_CON)) {
            this.buttonSendRateConfirmation.enabled = false;
            this.buttonSendRateConfirmation.disabledTooltip = "Only one rate confirmation can be sent at a time.";
            return;
        }
        if (selectedRows.length > getDispatchControlValue("max_assign_selections")) {
            this.buttonSendRateConfirmation.enabled = false;
            this.buttonSendRateConfirmation.disabledTooltip = CommonContextMenuItems.MAX_ROWS;
            return;
        }
    }

    private syncButtonLoadboard(selectedRows: TableRow[]) {

        if(!BrokeragePlanningToolPanel.pbwLoadBoardServicesExist){
            this.buttonLoadboard.enabled = false;
            this.buttonLoadboard.tooltip = null;
            this.buttonLoadboard.disabledTooltip = "No active load board services.";
            return;
        }else{
            this.buttonLoadboard.enabled = true;
            this.buttonLoadboard.disabledTooltip = null;
        }

        if (selectedRows.length > 1 && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTI_LOADBOARD)) {
            this.buttonLoadboard.disabledTooltip = ONLY_ONE_ORDER;
            this.buttonLoadboard.enabled = false;
            return;
        }

        if (selectedRows.length > getDispatchControlValue("max_loadboard_selections")) {
            this.buttonLoadboard.disabledTooltip = CommonContextMenuItems.MAX_ROWS;
            this.buttonLoadboard.enabled = false;
            return;
        }

        const postToLoadboardFilter = (row: ModelRow) => {
            const isNotOnHold = !row.getBoolean("hold", false);
            const isAvailableStatus = row.getString("move_status", null) === "A";
            const isNotBrLTL = !row.getBoolean("is_brokerage_ltl", false);
            const isNotManifestOnHold = !row.getBoolean("manifest_hold")
            return isNotOnHold && isAvailableStatus && isNotBrLTL && isNotManifestOnHold;
        }

        if (!selectedRows.every(row => postToLoadboardFilter(row.data))) {
            this.buttonLoadboard.disabledTooltip = INVALID_SELECTION;
            this.buttonLoadboard.enabled = false;
        }
    }

    private syncButtonRefreshLoadPosting(selectedRows: TableRow[]) {

        const refreshLoadboardFilter = (row: ModelRow) => {
            const isPostedToDat = row.getDateTime("loadboard_last_refresh", null) != null && Boolean(row.get("loadboard"));
            return isPostedToDat;
        }

        if (selectedRows.every(row => !refreshLoadboardFilter(row.data))) {
            this.buttonRefreshLoadPosting.disabledTooltip = "None of the selected orders are posted to the DAT load board.";
            this.buttonRefreshLoadPosting.enabled = false;
        }
    }

    private syncButtonTopMatch(selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            this.buttonTopMatch.disabledTooltip = ONLY_ONE_ORDER;
            this.buttonTopMatch.enabled = false;
        } else {
            const modelRow = selectedRows[0].data;
            if (modelRow?.getBoolean("is_brokerage_ltl")) {
                this.buttonTopMatch.disabledTooltip = "This order is a Brokerage LTL order";
                this.buttonTopMatch.enabled = false
            }
            if (modelRow?.getBoolean("manifest_hold")) {
                this.buttonTopMatch.disabledTooltip = "Top Match button is disabled because the manifest is on hold.";
                this.buttonTopMatch.enabled = false;
            }
        }
    }

    private syncButtonHold(selectedRows: TableRow[]) {
        if (!this.onHoldAction.validateSelection(selectedRows.map(tableRow => tableRow.data as ModelRow))) {
            this.buttonHold.disabledTooltip = INVALID_SELECTION;
            this.buttonHold.enabled = false;
        }
        if (selectedRows.length > getDispatchControlValue("max_hold_selections")) {
            this.buttonHold.disabledTooltip = CommonContextMenuItems.MAX_ROWS;
            this.buttonHold.enabled = false;
        }
    }

    private syncLtlOrderButton(button: Button, selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            button.disabledTooltip = ONLY_ONE_ORDER;
            button.enabled = false;
            return;
        }
        const modelRow = selectedRows[0].data;
        if (!modelRow?.getBoolean("is_brokerage_ltl")) {
            button.disabledTooltip = "This order is not a Brokerage LTL order";
            button.enabled = false;
            return;
        }

        if (button === this.buttonTerminalInfo) {
            const brltlOrder = {
                order_type_id: modelRow.get("order_type", null) as string | null,
                revenue_code_id: modelRow.get("revenue_code", null) as string | null,
                order_mode: modelRow.get("order_mode", null) as string | null
            };

            if (!BrltlUtil.isBRLTLOrder(brltlOrder)) {
                button.disabledTooltip = "Terminal Info is only available for BRLTL orders.";
                button.enabled = false;
            }
        }

    }

    private syncButtonConvertSpotToOrder(selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            this.buttonConvertSpotToOrder.disabledTooltip = ONLY_ONE_ORDER;
            this.buttonConvertSpotToOrder.enabled = false
        } else {
            const modelRow = selectedRows[0].data;
            if (!isSpotOrder(modelRow)) {
                this.buttonConvertSpotToOrder.disabledTooltip = "This order is not a spot order.";
                this.buttonConvertSpotToOrder.enabled = false;
            } else if (modelRow?.get("customer_id") == null) {
                this.buttonConvertSpotToOrder.disabledTooltip = "A customer must be assigned to this spot order before it can be converted to an order.";
                this.buttonConvertSpotToOrder.enabled = false;
            }
        }
    }

    private syncButtonReleaseToBilling(selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            this.buttonReleaseToBilling.disabledTooltip = ONLY_ONE_ORDER;
            this.buttonReleaseToBilling.enabled = false;
        } else {
            const modelRow = selectedRows[0].data;
            if (isSpotOrder(modelRow)) {
                this.buttonReleaseToBilling.disabledTooltip = "Release to invoicing is not available for spot orders.";
                this.buttonReleaseToBilling.enabled = false;
            } else if(isReleasedToBilling(modelRow)) {
                this.buttonReleaseToBilling.disabledTooltip = "This order has already been released to invoicing.";
                this.buttonReleaseToBilling.enabled = false;
            }
        }
    }

    syncButtonRemoveCarrier(selectedRows: TableRow[]) {
        if (PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTISELECT_REMOVE_CARRIER) && selectedRows.length > 1) {
            this.buttonRemoveCarrier.disabledTooltip = ONLY_ONE_ORDER
            this.buttonRemoveCarrier.enabled = false;
        }
        else if (selectedRows.length > getDispatchControlValue("max_unassign_selections")) {
            this.buttonRemoveCarrier.disabledTooltip = CommonContextMenuItems.MAX_ROWS;
            this.buttonRemoveCarrier.enabled = false;
        }
        else {
            const dispatchControlSettings = LMEGeneralSettings.getSingleton().getDispatchControl();
            const removeCarrierFilter = (row: ModelRow) => {
                const brokerage = row.getBoolean("brokerage", false);
                const carrierPopulated = row.getString("override_payee_id") != null && brokerage;
                const intercompanyEnabled = row.getBoolean("intercompany_enabled", false);
                const validStatus = ASSIGNED_STATUSES.includes(row.getString("move_status"));
                const updateAllowed = CommonContextMenuItems.hasUpdateOrderPermissions(row)
                                && CommonContextMenuItems.hasUpdateMovementPermissions(row);
                const revCodeRequiredAndNull = dispatchControlSettings.enforce_revenu_cod == 'Y' && row.getString("revenue_code") == null;
                const billToRequiredAndNull = dispatchControlSettings.enforce_billto == 'Y' && row.getString("customer_id") == null

                return (brokerage || (carrierPopulated && intercompanyEnabled))
                    && validStatus
                    && updateAllowed
                    && !revCodeRequiredAndNull
                    && !billToRequiredAndNull;
            }
            if (!selectedRows.every(row => removeCarrierFilter(row.data))) {
                this.buttonRemoveCarrier.disabledTooltip = INVALID_SELECTION;
                this.buttonRemoveCarrier.enabled = false;
            }
        }
    }

    syncButtonAssignButton(selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            DispatchUtil.areMultiAssignable(selectedRows, this.buttonAssign);
            this.buttonDispatch.enabled = false;

        } else if (selectedRows?.[0].data) {
            DispatchUtil.isAssignable(selectedRows[0].data, { btnAssign: this.buttonAssign, btnDispatch: this.buttonDispatch });
        }
    }

    syncButtonDispatchButton(selectedRows: TableRow[]) {
        if (selectedRows.length !== 1) {
            this.buttonDispatch.enabled = false;

        } else if (selectedRows?.[0].data) {
            DispatchUtil.isAssignable(selectedRows[0].data, { btnDispatch: this.buttonDispatch });
        }
    }

    syncButtonSendTenderButton(selectedRows: TableRow[]) {
        if (selectedRows.length > 1 && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTI_TENDERS)) {
            this.buttonSendTender.disabledTooltip = "Only one load tender can be sent at a time.";
            this.buttonSendTender.enabled = false;
            return;
        }
        if (selectedRows.length > getDispatchControlValue("max_assign_selections")) {
            this.buttonSendTender.disabledTooltip = CommonContextMenuItems.MAX_ROWS;
            this.buttonSendTender.enabled = false;
            return;
        }

        const isDfPayee = selectedRows[0]?.data.get("when_to_send_code") != null;

        if (!isDfPayee) {
            this.buttonSendTender.disabledTooltip = INVALID_DF_CARRIER;
            this.buttonSendTender.enabled = false;
            return;
        }

        const firstPayeeId = selectedRows[0]?.data.get("carrier_id");

        const allSamePayeeId = selectedRows.every(row => row.data.get("carrier_id") === firstPayeeId);

        if (!allSamePayeeId) {
            this.buttonSendTender.disabledTooltip = INVALID_LOAD_TENDER;
            this.buttonSendTender.enabled = false;
            return;
        }
    }

    /** This is an event handler for the onClick event of buttonAssign. */
    buttonAssignOnClick(event: ClickEvent) {
        this.buttonAssign.busy = true;
        if (this.table?.selectedRows.length > 1) {
            MultiCarrierAssignmentStepper.showAsSlideout(this.table.selectedRows, this.buttonAssign);
        }
        else {
            const modelRow = this.table?.selectedRow?.data;
            if (modelRow != null) {
                this.table.selectedRow.expanded = false;
                CarrierAssignmentStepper.showAsSlideout({
                    isOfferView: false,
                    movementId: modelRow.get("movement_id"),
                    orderId: modelRow.get("order_id"),
                    carrierAssignmentButton: event.target as Button,
                    manifestId: modelRow.get("manifest_id")
                });
            }
            this.buttonAssign.busy = false;
        }
    }

    /** This is an event handler for the onClick event of buttonDispatch. */
    async buttonDispatchOnClick(event: ClickEvent) {
        CommonContextMenuItems.buttonDispatchOnClick(this.table.selectedRows[0], this.buttonDispatch);
    }

    async buttonSendRateConOnClick(event: ClickEvent) {
        const rows = this.table?.selectedRows;
        const modelRow = rows[0].data;
        if (modelRow == null)
            return;

        const button = event.target as Button;
        this.showMultiSendRateConfirmation(this.table?.selectedRows, button);
    }

    async showMultiSendRateConfirmation(selectedRows: TableRow[], sendRateConButton: Button) {
        if (selectedRows.length > 1) {
            MultiSendRateConfirmation.showSlideout(selectedRows);
        }
        else {
            SendRateConfirmation.showSendRateConfirmationSlideout(sendRateConButton, selectedRows[0].data.get("movement_id"));
        }
    }

    /** This is an event handler for the onClick event of buttonLoadboard. */
    buttonLoadboardOnClick(event: ClickEvent) {
        const selectedRows = this.table?.selectedRows ?? [];
        if (selectedRows.length === 1) {
            this.handleLoadboardSingleSelection(selectedRows[0]);
            return;
        }

        PostToLoadBoards.showMultiSelectSlideout(selectedRows);
    }

    determineLoadboardMovementId(selectedRow: TableRow) {
        const data = selectedRow?.data;
        const origStopType = data?.get("origin_stop_type");
        const destStopType = data?.get("destination_stop_type");

        const validStopTypes = ['SP', 'SD'];
        return data?.get("posted_by_movement") ||
            (!data?.get("loadboard") && (validStopTypes.includes(origStopType) || validStopTypes.includes(destStopType))) ?
            data?.get("movement_id") : null;
    }

    handleLoadboardSingleSelection(selectedRow: TableRow) {
        const data = selectedRow?.data;
        const exceptionReasons = data?.get("loadboard_exception_list");
        const pbwLoadboardServices = data?.get("load_board_vendor_list");
        const orderId = data?.get("order_id");
        const movementId = this.determineLoadboardMovementId(selectedRow);

        const originStopId = movementId === null ? data.get("order_origin_stop_id") : data.get("origin_stop_id");

        const isPostedByMovement = data?.get("posted_by_movement");
        const isPostedMovementRowSelected = data?.get("is_posted_movement");
        let postTitle = "Post to Load Boards - Order " + orderId;
        if (movementId !== null) {
            const origin = CityUtil.formatCityStateZip(data?.get("origin_city"), data?.get("origin_state"));
            const destination = CityUtil.formatCityStateZip(data?.get("destination_city"), data?.get("destination_state"));
            postTitle += `\t${origin} to ${destination}`;
        }

        if (isPostedByMovement && !isPostedMovementRowSelected) {
            CommonDialogs.showYesNo("Another movement for this order is currently posted. Do you want to replace the currently posted movement information with the selected movement?", "Update Posted Movement").then(result => {
                if (result === true)
                    PostToLoadBoards.showInSlideoutDecorator(orderId, postTitle, originStopId, movementId, exceptionReasons, true, pbwLoadboardServices);
                else if (result === false || result === undefined)
                    return;
            });
        } else {
            PostToLoadBoards.showInSlideoutDecorator(orderId, postTitle, originStopId, movementId, exceptionReasons, false,pbwLoadboardServices);
        }
    }

    /** This is an event handler for the onClick event of buttonTopMatch. */
    buttonTopMatchOnClick(event: ClickEvent) {
        const data = this.table?.selectedRow?.data;
        const queryParams: Record<string, string> = {
            orderId: data?.get("order_id"),
            movementId: data?.get("movement_id"),
            trailerType: data?.get("equipment_type"),
            tmProfile: LMEUserSettings.get()?.tm_profile,
            bpProfile: LMEUserSettings.get()?.bp_profile,
            autoSearch: "true"
        }

        ObjectUtil.deleteNullOrEmptyProps(queryParams);

        let params = "";
        if (!ObjectUtil.isEmptyObject(queryParams))
            params = "?" + new URLSearchParams(queryParams);
        if (queryParams.tmProfile != null || this.bpTmProfile != null) {
            Navigation.navigateTo("lme/powerbroker/TopMatchList" + params.toString(), {
                width: window.screen.availWidth,
                height: window.screen.availHeight,
                newTab: true,
                windowDecorators: false
            });
        } else {
            const panel = new Panel();
                const label = new Label({ text: "No TopMatch profile set. Please update in your User Settings." });
                const button = new Button({
                    caption: "Open User Settings",
                    onClick: () => {
                        UserLayoutProvider.navigateTo();
                        this.noProfileSnackbar.dismiss();
                    }
                });
            panel.add(label);
            panel.add(button);
            this.noProfileSnackbar = Snackbar.showWarningSnackbar(panel, {millisUntilDismissal: 5000});
        }
    }

    /** This is an event handler for the onClick event of buttonConvertSpotToOrder. */
    buttonConvertSpotToOrderOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null) return;
        button.busy = true;
        Api.post("lme/dispatch/convert-spot-to-order", { spot_id: modelRow.get("order_id") }).then(output => {
            const errorReason = output.data[0].error_reason;
            if (errorReason != null) {
                Snackbar.showWarningSnackbar(errorReason);
            } else {
                const orderId = output.data[0].order_id;
                Toast.showSuccessToast("Spot order converted to order " + orderId);
            }
        }).finally(() => {
            button.busy = false;
        });
    }

    /** This is an event handler for the onClick event of buttonLaneHistory. */
    buttonLaneHistoryOnClick(event: ClickEvent) {
        const data = this.table?.selectedRow?.data;
        if (data == null) return;

        const button = event.target as Button;
        button.busy = true;
        const laneHistoryLayout: LaneHistory = Layout.getLayout("lme/dispatch/LaneHistory") as LaneHistory;
        laneHistoryLayout.addLoadErrorListener(() => button.busy = false);

        new SlideoutDecorator({
            layout: laneHistoryLayout,
            layoutLoadListeners: () => {
                laneHistoryLayout.sourceLaneHistory.searchRow.set("find_near_radius", 150);
                laneHistoryLayout.sourceLaneHistory.searchRow.set("customer.id", data.get("customer_id"));
                laneHistoryLayout.sourceLaneHistory.searchRow.setLookupModelData("customer.id", {
                    id: data.get("customer_id"),
                    name: data.get("customer_name")
                });
                laneHistoryLayout.sourceLaneHistory.searchRow.set("name", data.get("customer_name"));
                laneHistoryLayout.sourceLaneHistory.searchRow.set("orders.equipment_type_id", data.get("equipment_type"));
                laneHistoryLayout.sourceLaneHistory.searchRow.setLookupModelData("orders.equipment_type_id", {
                    id: data.get("equipment_type")
                });

                laneHistoryLayout.sourceLaneHistory.searchRow.set("stop_origin.state", data.get("origin_state"));
                laneHistoryLayout.sourceLaneHistory.searchRow.setLookupModelData("stop_origin.state", {
                    id: data.get("origin_state")
                });
                laneHistoryLayout.sourceLaneHistory.searchRow.set("stop_origin.city_id", data.get("origin_city_id"));
                laneHistoryLayout.sourceLaneHistory.searchRow.set("stop_origin.city_name", data.get("origin_city"));
                laneHistoryLayout.sourceLaneHistory.searchRow.set("stop_dest.state", data.get("destination_state"));
                laneHistoryLayout.sourceLaneHistory.searchRow.setLookupModelData("stop_dest.state", {
                    id: data.get("destination_state")
                });

                laneHistoryLayout.searchpanelFilters.displayData(laneHistoryLayout.sourceLaneHistory.searchRow, null, 0);
                laneHistoryLayout.citystateStopOriginCityId.textCity.displayData(laneHistoryLayout.sourceLaneHistory.searchRow, null, 0);
                laneHistoryLayout.citystateStopOriginCityId.textState.displayData(laneHistoryLayout.sourceLaneHistory.searchRow, null, 0);
                laneHistoryLayout.citystateStopOriginCityId.textZip.displayData(laneHistoryLayout.sourceLaneHistory.searchRow, null, 0);
                laneHistoryLayout.citystateStopDestCityId.textCity.displayData(laneHistoryLayout.sourceLaneHistory.searchRow, null, 0);
                laneHistoryLayout.citystateStopDestCityId.textState.displayData(laneHistoryLayout.sourceLaneHistory.searchRow, null, 0);
                laneHistoryLayout.citystateStopDestCityId.textZip.displayData(laneHistoryLayout.sourceLaneHistory.searchRow, null, 0);
                laneHistoryLayout.panelSummary.displayData(laneHistoryLayout.sourceLaneHistory.searchRow, null, 0);
                laneHistoryLayout.setSummaryPanel();
                if (data.get("customer_id") != null) {
                    laneHistoryLayout.sourceCustomer.search({ id: data.get("customer_id") });
                }
                laneHistoryLayout.layoutheaderLaneHist.visible = false;
                laneHistoryLayout.panelSummary.borderWidth = 0;
            },
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            title: "Lane History",
            transitionOptions: { direction: Alignment.CENTER },
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            onClose: (cancelled: boolean) => {
                laneHistoryLayout.slideOut()
            },
            doAfterSlideIn: () => {
                button.busy = false
                laneHistoryLayout.defaultDateRange();
                laneHistoryLayout.switchSearchMode.checked = true;
                laneHistoryLayout.mainDataSource.executeValidatedSearch();
            }
        });
    }

    /** This is an event handler for the onClick event of buttonReleaseToBilling. */
    buttonReleaseToBillingOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null) return;
        button.busy = true;
        const order_id = modelRow.get("order_id");
        Api.post("lme/powerbroker/release-to-billing", { order_id }).then(output => {
            const success = output.data[0].success;
            const error_message = output.data[0].error_message;
            if (success) {
                Toast.showSuccessToast("Released to Invoicing", "Order " + order_id);
            } else {
                Toast.showToast(error_message);
            }
        }).finally(() => {
            button.busy = false;
        });
    }

    /** This is an event handler for the onClick event of buttonCarrierSelection. */
    buttonCarrierSelectionOnClick(event: ClickEvent) {
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null) return;
        CarrierSelectionStepper.showSlideout({ orderId: modelRow.get("order_id") });
    }

    /** This is an event handler for the onClick event of buttonTerminalInfo. */
    buttonTerminalInfoOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const modelRow = this.table?.selectedRow?.data;
        if (modelRow == null) return;
        try {
            button.busy = true;
            const orderId = modelRow.get("order_id");
            BrltlUtil.getTerminalInfo(orderId);
        } finally {
            button.busy = false;
        }
    }

    async buttonOnHoldOnClick(event: ClickEvent) {
        const selectedRows = this.table.selectedRows;
        const isOnHold = selectedRows?.[0].data.getBoolean("hold");
        this.onHoldAction.execute({isOnHold, selectedRows});
    }

    async buttonRefreshLoadPostingOnClick(event: ClickEvent) {
        const selectedRows = this.table.selectedRows;
        const validRows = selectedRows.filter(row => row.data.getDateTime("loadboard_last_refresh") != null);
        const invalidRowCount = selectedRows.length - validRows.length;
        try {
            if (validRows.length > 0) {
                await Api.post("lme/general/pnn/dat-loadboard-refresh",
                    {
                        order_ids: validRows.map(row => ({
                            order_id: row.data.get("order_id")
                        }))
                    }).then(response => {
                        if (response.data?.[0]?.["all_loads_have_refresh_time_error"]) {
                            Snackbar.showWarningSnackbar("All of the selected orders were posted or refreshed within the last 15 minutes and are currently unavailable for refresh.");
                        }
                        else if (response.data?.[0]?.["some_loads_have_refresh_time_error"]) {
                            Snackbar.showWarningSnackbar("One or more of the selected orders were posted or refreshed within the last 15 minutes and are currently unavailable for refresh. The system will refresh only the orders that are eligible.");
                        } else if (response.data?.[0]?.["dat_error"]) {
                            Snackbar.showWarningSnackbar("One or more of the selected orders failed to refresh on the DAT load board.");
                        }

                        if (invalidRowCount > 0) {
                            Snackbar.showWarningSnackbar("One or more of the selected orders are not posted to the DAT load board and will be excluded. Only loads posted to DAT will be processed for refresh");
                        }

                        if (response.data?.[0]?.["success_count"] > 0) {
                            Toast.showSuccessToast("The refresh successfully updated " + response.data?.[0]?.["success_count"] + " order(s) to the DAT load board.");
                        }
                    });
            }
        } finally {
            this.table.selectedRows = [];
        }
    }

    private get selectedRowsOrderIds(): any[] {
        return this.table.selectedRows.map(tableRow => {
            return {
                order_ids: tableRow.data.get("order_id")
            };
        });
    }

    async buttonRemoveCarrierOnClick(event: ClickEvent) {
        const selectedRows = this.table.selectedRows;
        return UnassignCarrierPrompt.showMultiPrompt(selectedRows);
    }

    async buttonUpdateFieldOnClick(event: ClickEvent) {
        CommonContextMenuItems.buttonUpdateFieldOnClick(this.table.selectedRows);
    }

    buttonSendTenderOnClick(event: ClickEvent) {
        const rows = this.table?.selectedRows;
        if (rows.length > 1) {
            MultiSendLoadTenderStepper.showSlideout(rows);
        }
        else {
            SendLoadTender.sendLoadTender(rows[0].data.get("carrier_id"), rows[0].data.get("movement_id"), "N");
        }
    }

    buttonRateOrdersOnClick(event: ClickEvent) {
        CommonContextMenuItems.buttonRateOrdersOnClick(this.table.selectedRows);
    }

    public getContextMenuItems(tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        const tableType = BrokerageMovementTableType.getTypeForStatus(tableRow.data.getString("move_status"));
        if (tableType == null)
            return [];

        this.syncButtonVisiblity(tableType);
        let selectedRows = tableRow.table.selectedRows;
        if (!selectedRows.includes(tableRow)) {
            tableRow.table.selectedRow = tableRow;
            selectedRows = [tableRow]
        }

        const isOnHold = selectedRows[0]?.data.getBoolean("hold");

        const contextItems = [
            CommonContextMenuItems.createAssignCarrierFromSelected(selectedRows, this.buttonAssign),
            CommonContextMenuItems.createDispatchCarrierFromSelected(selectedRows, this.buttonDispatch),
            ...ContextMenuItemCreator.fromButtons(
                { button: this.buttonSendTender, text: "Send Load Tender" },
                { button: this.buttonLoadboard, text: "Post to Load Board" },
                { button: this.buttonTopMatch, text: "TopMatch" },
                { button: this.buttonConvertSpotToOrder, text: "Convert to Spot Order" },
                { button: this.buttonLaneHistory, text: "Lane History" },
                { button: this.buttonReleaseToBilling, text: "Release to Invoicing" },
                { button: this.buttonCarrierSelection, text: "Carrier Selection" },
                { button: this.buttonTerminalInfo, text: "Terminal Info" }
            ),
            CommonContextMenuItems.createSendRateConfirmationItemFromSelected(selectedRows),
            ...ContextMenuItemCreator.fromButtons(
                { button: this.buttonHold, text: isOnHold ? "Remove Hold" : "Apply Hold" },
                { button: this.buttonRemoveCarrier, text: "Remove Carrier" },
                { button: this.buttonUpdateField, text: "Update Field" },
            ),
            CommonContextMenuItems.createRateOrdersItemFromSelected(selectedRows),
            CommonContextMenuItems.createRecentChangesItemFromSelected(selectedRows),
        ];

        if (contextItems.filter(item => item != null && item.visible).length > 0) {
            return contextItems;
        } else {
            return [{ text: "No Available Multi-Select Actions" }];
        }
    }

}
